/**
 * DashboardPage — system status overview for the Printsonalities admin.
 *
 * Cards:
 *  - Aktuelle Zahlen   — live order/draft/review/shipment counts
 *  - Letzter Sync      — from config/orderSync
 *  - Auto-Sync         — from config/syncSettings
 *
 * Below:
 *  - Scraper-Aktivität — 14-day × 24h heatmap from scraper_activity collection
 *  - Aktive Bestellungen — 30-day stacked area chart from orders_snapshot
 *  - Admin-Notizen     — real-time notes with unread indicator
 */
import React, { useEffect, useState, useRef } from "react";
import {
  collection, doc, getDoc, getDocs, addDoc, deleteDoc,
  orderBy, query, onSnapshot, where, serverTimestamp,
} from "firebase/firestore";
import { getAuth } from "firebase/auth";
import { db } from "../firebase";
import { useNavigate } from "react-router-dom";
import ActivityHeatmap from "./ActivityHeatmap";
import OrdersStackedChart from "./OrdersStackedChart";
import PendingMessagesAlert from "./PendingMessagesAlert";
import OrderIssuesAlert from "./OrderIssuesAlert";

// ── Helpers ──────────────────────────────────────────────────────────────────

function fmtTs(ts) {
  if (!ts) return "—";
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  return d.toLocaleString("de-DE", {
    day: "2-digit", month: "2-digit", year: "numeric",
    hour: "2-digit", minute: "2-digit",
  });
}

function fmtDuration(secs) {
  if (secs == null) return "—";
  if (secs < 60) return `${secs}s`;
  return `${Math.floor(secs / 60)}m ${Math.round(secs % 60)}s`;
}

function berlinHour() {
  return new Date().toLocaleString("en-US", {
    timeZone: "Europe/Berlin", hour: "numeric", hour12: false,
  }).padStart(2, "0") | 0;
}

const ACTIVE_STAGES = new Set([
  "new", "grafiker", "entwurf_raus", "produktion", "in_produktion", "druck", "kerzen",
]);

const NOTES_LS_KEY = "dashboardNotesLastSeen";

// ── Styles ───────────────────────────────────────────────────────────────────

const card = {
  background: "#fff",
  borderRadius: "12px",
  boxShadow: "0 1px 4px rgba(0,0,0,0.08)",
  padding: "1.5em 2em",
  marginBottom: "1.5em",
};

const cardTitle = {
  fontWeight: "700",
  fontSize: "0.78rem",
  color: "#a0aec0",
  textTransform: "uppercase",
  letterSpacing: "0.06em",
  marginBottom: "1em",
};

const badge = (color) => ({
  display: "inline-block",
  padding: "0.2em 0.7em",
  borderRadius: "999px",
  fontSize: "0.78rem",
  fontWeight: "600",
  background: color === "green"  ? "#d1fae5"
            : color === "orange" ? "#ffedd5"
            : color === "red"    ? "#fee2e2"
            : color === "blue"   ? "#dbeafe"
            :                      "#f1f5f9",
  color: color === "green"  ? "#065f46"
       : color === "orange" ? "#9a3412"
       : color === "red"    ? "#991b1b"
       : color === "blue"   ? "#1e40af"
       :                      "#475569",
});

// ── Component ─────────────────────────────────────────────────────────────────

export default function DashboardPage() {
  const navigate = useNavigate();

  const [orderSync,    setOrderSync]    = useState(null);
  const [syncSettings, setSyncSettings] = useState(null);
  const [etsyAuth,     setEtsyAuth]     = useState(null);
  const [stats,        setStats]        = useState(null);
  const [loading,      setLoading]      = useState(true);

  // Notes state
  const [notes,       setNotes]       = useState([]);
  const [noteText,    setNoteText]    = useState("");
  const [noteSaving,  setNoteSaving]  = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const lastSeenRef = useRef(
    parseInt(localStorage.getItem(NOTES_LS_KEY) || "0", 10)
  );

  useEffect(() => {
    async function load() {
      try {
        const [syncSnap, settingsSnap, authSnap] = await Promise.all([
          getDoc(doc(db, "config", "orderSync")),
          getDoc(doc(db, "config", "syncSettings")),
          getDoc(doc(db, "config", "etsyAuth")),
        ]);
        setOrderSync(syncSnap.exists() ? syncSnap.data() : null);
        setSyncSettings(settingsSnap.exists() ? settingsSnap.data() : null);
        if (authSnap.exists()) setEtsyAuth(authSnap.data());

        // Stats — load in parallel
        const now = new Date();
        const [ordersSnap, draftsSnap, reviewsSnap, shipSnap] = await Promise.all([
          getDocs(collection(db, "etsy_orders")),
          getDocs(collection(db, "drafts")),
          getDocs(query(collection(db, "etsy_reviews"), where("status", "==", "pending"))),
          getDocs(query(collection(db, "shipping_queue"), where("status", "==", "pending"))),
        ]);

        const allOrders = ordersSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
        const activeOrders = allOrders.filter((o) => ACTIVE_STAGES.has(o.workflowStage)).length;
        const pendingConfirmations = allOrders.filter(
          (o) => o.workflowStage === "new" && o.welcomeSentAt && !o.orderReviewedAt
        ).length;
        const pendingDrafts = draftsSnap.docs.filter((d) => {
          const data = d.data();
          return !data.reviewedAt && !data.draftArchived;
        }).length;
        const scheduledShipments = shipSnap.docs.filter((d) => {
          const sched = d.data().scheduledAt;
          return sched && (sched.toDate ? sched.toDate() : new Date(sched)) > now;
        }).length;

        setStats({
          activeOrders,
          pendingConfirmations,
          pendingDrafts,
          openReviews: reviewsSnap.size,
          scheduledShipments,
        });
      } catch (e) {
        console.error("DashboardPage load error:", e);
      } finally {
        setLoading(false);
      }
    }
    load();

    // Real-time notes listener
    const unsub = onSnapshot(
      query(collection(db, "dashboard_notes"), orderBy("createdAt", "desc")),
      (snap) => {
        const loaded = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        setNotes(loaded);
        const cutoff = lastSeenRef.current;
        const unread = loaded.filter((n) => {
          if (!n.createdAt) return false;
          const t = n.createdAt.toDate ? n.createdAt.toDate().getTime() : n.createdAt;
          return t > cutoff;
        }).length;
        setUnreadCount(unread);
      }
    );

    // Mark all current notes as seen on this visit
    const now = Date.now();
    localStorage.setItem(NOTES_LS_KEY, String(now));
    lastSeenRef.current = now;

    return () => unsub();
  }, []);

  async function saveNote() {
    if (!noteText.trim()) return;
    setNoteSaving(true);
    try {
      const auth = getAuth();
      const user = auth.currentUser;
      const author = user?.displayName || user?.email || "Admin";
      await addDoc(collection(db, "dashboard_notes"), {
        text: noteText.trim(),
        author,
        createdAt: serverTimestamp(),
      });
      setNoteText("");
    } catch (e) {
      alert("Fehler beim Speichern: " + e.message);
    } finally {
      setNoteSaving(false);
    }
  }

  async function deleteNote(noteId) {
    try {
      await deleteDoc(doc(db, "dashboard_notes", noteId));
    } catch (e) {
      alert("Fehler beim Löschen: " + e.message);
    }
  }

  if (loading) {
    return <p style={{ color: "#718096" }}>Lade…</p>;
  }

  // ── Sync status ─────────────────────────────────────────────────────────────
  const syncStatus = orderSync?.status ?? "—";
  const syncColor  = syncStatus === "done"    ? "green"
                   : syncStatus === "error"   ? "red"
                   : syncStatus === "running" ? "blue"
                   :                            "gray";

  // ── Etsy token freshness ─────────────────────────────────────────────────────
  const tokenExpiresAtMs = etsyAuth?.expiresAtMs || 0;
  const msLeft      = tokenExpiresAtMs - Date.now();
  const tokenColor  = msLeft <= 0              ? "red"
                    : msLeft < 10 * 60 * 1000  ? "orange"
                    :                            "green";
  const tokenLabel  = msLeft <= 0             ? "Abgelaufen"
                    : msLeft < 10 * 60 * 1000 ? "Läuft bald ab"
                    :                           "Token aktiv";

  // ── Auto-sync ───────────────────────────────────────────────────────────────
  const autoEnabled = syncSettings?.autoSyncEnabled ?? false;
  const startHour   = syncSettings?.startHour ?? 8;
  const endHour     = syncSettings?.endHour ?? 23;
  const nowHour     = berlinHour();
  const inWindow    = autoEnabled && nowHour >= startHour && nowHour < endHour;
  const autoLabel   = !autoEnabled ? "Deaktiviert"
                    : inWindow     ? `Aktiv (${startHour}–${endHour} Uhr)`
                    :                `Außerhalb Fenster (${startHour}–${endHour} Uhr)`;
  const autoColor   = !autoEnabled ? "gray" : inWindow ? "green" : "orange";

  // ── Stat rows ───────────────────────────────────────────────────────────────
  const statRows = stats ? [
    {
      label: "Aktive Bestellungen",
      value: stats.activeOrders,
      color: "#1a202c",
      href: "/orders",
    },
    {
      label: "Warten auf Bestätigung",
      value: stats.pendingConfirmations,
      color: stats.pendingConfirmations > 0 ? "#c05621" : "#718096",
      href: "/order-confirmations",
    },
    {
      label: "Entwurf ausstehend",
      value: stats.pendingDrafts,
      color: stats.pendingDrafts > 0 ? "#c05621" : "#718096",
      href: "/overview",
    },
    {
      label: "Offene Bewertungen",
      value: stats.openReviews,
      color: stats.openReviews > 0 ? "#553c9a" : "#718096",
      href: "/reviews",
    },
    {
      label: "Geplante Versendungen",
      value: stats.scheduledShipments,
      color: stats.scheduledShipments > 0 ? "#276749" : "#718096",
      href: "/shipping",
    },
  ] : [];

  return (
    <div style={{ maxWidth: 900, margin: "0 auto" }}>
      <h2 style={{ fontWeight: "700", fontSize: "1.3rem", marginBottom: "1.5em", color: "#1a202c" }}>
        Dashboard
      </h2>

      {/* ── Alerts (hidden if none) — Etsy-Message-Failures needing manual action ── */}
      {/* Hängende oder falsch behandelte Aufträge (Versand, Stufenwechsel, Versand-Kontrolle) */}
      <OrderIssuesAlert />
      <PendingMessagesAlert />

      {/* ── Status cards ── */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: "1.5em", marginBottom: "2em" }}>

        {/* Stats card */}
        <div style={card}>
          <div style={cardTitle}>Aktuelle Zahlen</div>
          {stats ? (
            <div style={{ display: "flex", flexDirection: "column", gap: "0.55em" }}>
              {statRows.map(({ label, value, color, href }) => (
                <div
                  key={label}
                  onClick={() => href && navigate(href)}
                  style={{
                    display: "flex", justifyContent: "space-between", alignItems: "center",
                    cursor: href ? "pointer" : "default",
                    padding: "0.2em 0",
                    borderBottom: "1px solid #f0f4f8",
                  }}
                >
                  <span style={{ fontSize: "0.8rem", color: "#4a5568" }}>{label}</span>
                  <span style={{ fontWeight: "700", fontSize: "1.05rem", color }}>{value}</span>
                </div>
              ))}
            </div>
          ) : (
            <p style={{ color: "#718096", fontSize: "0.82rem" }}>Lädt…</p>
          )}
        </div>

        {/* Last sync */}
        <div style={card}>
          <div style={cardTitle}>Letzter Sync</div>
          <div style={{ marginBottom: "0.4em" }}>
            <span style={badge(syncColor)}>
              {syncStatus === "done"    ? "Erfolgreich"
             : syncStatus === "error"  ? "Fehler"
             : syncStatus === "running"? "Läuft…"
             : syncStatus === "idle"   ? "Bereit"
             : syncStatus}
            </span>
          </div>
          {orderSync?.timeoutAborted && (
            <div style={{ fontSize: "0.78rem", color: "#c53030", marginBottom: "0.3em", fontWeight: "600" }}>
              Abbruch wegen Timeout
            </div>
          )}
          {orderSync?.errorMessage && !orderSync?.timeoutAborted && (
            <div style={{ fontSize: "0.78rem", color: "#c53030", marginBottom: "0.3em" }}>
              {orderSync.errorMessage}
            </div>
          )}
          <div style={{ fontSize: "0.82rem", color: "#718096" }}>
            {orderSync?.finishedAt ? fmtTs(orderSync.finishedAt) : "—"}
            {orderSync?.durationSeconds != null && (
              <span> · {fmtDuration(orderSync.durationSeconds)}</span>
            )}
          </div>
          {syncStatus === "done" && (
            <div style={{ fontSize: "0.78rem", color: "#4a5568", marginTop: "0.4em" }}>
              +{orderSync?.count ?? 0} neu · {orderSync?.updatedCount ?? 0} aktualisiert · {orderSync?.archivedCount ?? 0} archiviert
            </div>
          )}
          {etsyAuth && (
            <div style={{ marginTop: "0.5em", borderTop: "1px solid #e2e8f0", paddingTop: "0.4em" }}>
              <span style={badge(tokenColor)}>{tokenLabel}</span>
              <div style={{ fontSize: "0.78rem", color: "#718096", marginTop: "0.25em" }}>
                Etsy Token · {fmtTs(etsyAuth.updatedAt)}
              </div>
            </div>
          )}
        </div>

        {/* Auto-sync */}
        <div style={card}>
          <div style={cardTitle}>Auto-Sync</div>
          <div style={{ marginBottom: "0.4em" }}>
            <span style={badge(autoColor)}>{autoLabel}</span>
          </div>
          <div style={{ fontSize: "0.82rem", color: "#718096" }}>
            Aktuelle Stunde (Berlin): {nowHour}:xx
          </div>
          {autoEnabled && (
            <div style={{ fontSize: "0.78rem", color: "#4a5568", marginTop: "0.3em" }}>
              Fenster: {startHour}:00 – {endHour}:00 Uhr
            </div>
          )}
        </div>
      </div>

      {/* ── Admin notes ── */}
      <div style={{ ...card, padding: "1.5em" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "0.6em", marginBottom: "1em" }}>
          <div style={cardTitle}>Admin-Notizen</div>
          {unreadCount > 0 && (
            <span style={{
              background: "#ed8936", color: "#fff",
              borderRadius: "999px", fontSize: "0.7rem", fontWeight: "700",
              padding: "0.15em 0.55em", lineHeight: 1.4,
              marginTop: "-0.15em",
            }}>
              {unreadCount} neu
            </span>
          )}
        </div>

        {/* Input */}
        <div style={{ display: "flex", gap: "0.75em", marginBottom: "1.25em" }}>
          <textarea
            value={noteText}
            onChange={(e) => setNoteText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) saveNote();
            }}
            placeholder="Notiz eingeben… (Strg+Enter zum Speichern)"
            maxLength={500}
            rows={3}
            style={{
              flex: 1, padding: "0.6em 0.8em",
              border: "1px solid #cbd5e0", borderRadius: "8px",
              fontSize: "0.875rem", color: "#2d3748",
              resize: "vertical", fontFamily: "inherit",
              lineHeight: "1.5",
            }}
          />
          <div style={{ display: "flex", flexDirection: "column", justifyContent: "flex-end", gap: "0.4em" }}>
            <button
              onClick={saveNote}
              disabled={noteSaving || !noteText.trim()}
              style={{
                background: "#4a2c2a", color: "#fff", border: "none",
                borderRadius: "8px", padding: "0.55em 1.2em",
                fontWeight: "600", fontSize: "0.85rem", cursor: "pointer",
                opacity: !noteText.trim() ? 0.5 : 1,
              }}
            >
              {noteSaving ? "…" : "Speichern"}
            </button>
            <div style={{ fontSize: "0.72rem", color: "#a0aec0", textAlign: "right" }}>
              {noteText.length}/500
            </div>
          </div>
        </div>

        {/* Notes list */}
        {notes.length === 0 ? (
          <p style={{ fontSize: "0.85rem", color: "#a0aec0", fontStyle: "italic" }}>
            Noch keine Notizen.
          </p>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: "0.75em" }}>
            {notes.map((note) => {
              const ts = note.createdAt?.toDate
                ? note.createdAt.toDate().getTime()
                : null;
              const isUnread = ts && ts > lastSeenRef.current - 100; // small buffer
              return (
                <div
                  key={note.id}
                  style={{
                    background: isUnread ? "#fffbeb" : "#f7fafc",
                    border: isUnread ? "1px solid #f6e05e" : "1px solid #e2e8f0",
                    borderRadius: "8px",
                    padding: "0.75em 1em",
                    display: "flex",
                    gap: "0.75em",
                    alignItems: "flex-start",
                  }}
                >
                  <div style={{ flex: 1 }}>
                    <p style={{
                      margin: "0 0 0.4em", fontSize: "0.875rem",
                      color: "#2d3748", whiteSpace: "pre-wrap", lineHeight: "1.5",
                    }}>
                      {note.text}
                    </p>
                    <div style={{ fontSize: "0.72rem", color: "#a0aec0" }}>
                      {note.author || "Admin"}{note.createdAt ? ` · ${fmtTs(note.createdAt)}` : ""}
                      {isUnread && (
                        <span style={{
                          marginLeft: "0.5em", background: "#ed8936",
                          color: "#fff", borderRadius: "999px",
                          padding: "0.05em 0.45em", fontSize: "0.68rem", fontWeight: "700",
                        }}>
                          neu
                        </span>
                      )}
                    </div>
                  </div>
                  <button
                    onClick={() => deleteNote(note.id)}
                    title="Notiz löschen"
                    style={{
                      background: "none", border: "none", cursor: "pointer",
                      color: "#cbd5e0", fontSize: "1.1rem", lineHeight: 1,
                      padding: "0.1em 0.2em", flexShrink: 0,
                      transition: "color 0.15s",
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.color = "#c53030"}
                    onMouseLeave={(e) => e.currentTarget.style.color = "#cbd5e0"}
                  >
                    ×
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* ── Scraper activity heatmap (replaces old sync-log table) ── */}
      <ActivityHeatmap />

      {/* ── Orders per stage — 30-day stacked area chart ── */}
      <OrdersStackedChart />
    </div>
  );
}
